import _ from 'lodash';
import moment from 'moment';
import { useEffect, useImperativeHandle, useRef, useState, type ForwardedRef } from 'react';
import { fetchHolterEvents, formatEventStrip, updateEventChangeAndNew } from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  createChartData,
  createMinutelyHrChartData,
  filterHourlySummaryRanges,
  getMinMaxChart,
  validateEcgDataMap,
} from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectEventChanges,
  selectEventNews,
  selectReloadHrChart,
  selectSelectedDateValue,
  useReportStore,
} from '@/entities/report';
import type { HrChartProps, HrChartRef } from './dailySummaryHrChart';

/**
 * Builds the daily heart-rate chart of the HR summary tab from the study's hourly ECG summaries and the artifact events of the selected date. Fetches those holter events, merges unsaved event changes into them, turns the minute-level average, max and min series into chart points, guides and event zones, refreshes the day's min and max HR header, and exposes the chart's arrow navigation through the forwarded ref.
 *
 * @param props - The chart props with the study and profile ids, the ECG data map and the timezone offset.
 * @param ref - The forwarded ref through which the parent triggers arrow navigation.
 * @returns An object carrying the tab store key, the chart ref and the computed chart data.
 */
const useDailySummaryHrChart = (
  { ecgDataMap, timezoneOffset, studyId, profileId }: HrChartProps,
  ref: ForwardedRef<HrChartRef>,
) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const minHrTabActive = useReportStore((s) => s.minHrTabActive);
  /**
   * Reads the baseline heart-rate summary shown above the chart. Pulls hrDataDefault straight off the current report store snapshot rather than subscribing.
   *
   * @returns The default heart-rate summary.
   */
  const getHrDataDefault = () => useReportStore.getState().hrDataDefault;
  const setHrDataDefault = useReportStore((s) => s.actions.setHrDataDefault);
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Toggles the loading state of the heart-rate chart. Forwards the value to the report store's setIsLoadingHrChart action under this tab's key.
   *
   * @param v - The new loading flag, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Stores the date the chart and the ECG viewer should jump to. Forwards the value to the report store's setJumpDate action under this tab's key.
   *
   * @param v - The new jump date, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Marks the heart-rate chart as having no data for the selected date. Forwards the value to the report store's setIsEmptyData action under this tab's key.
   *
   * @param v - The new empty-data flag, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  /**
   * Reads the merged per-day heart-rate summaries held in the report store. Pulls dailySummariesCombine straight off the current store snapshot rather than subscribing.
   *
   * @returns The combined daily summaries.
   */
  const getDailySummariesCombine = () => useReportStore.getState().dailySummariesCombine;
  const setCombinedEventsAction = useReportStore((s) => s.actions.setCombinedEvents);
  /**
   * Stores the artifact events of the selected date merged with the pending changes. Forwards the value to the report store's setCombinedEvents action under this tab's key.
   *
   * @param v - The new combined event list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setCombinedEvents = (v: Parameters<typeof setCombinedEventsAction>[1]) =>
    setCombinedEventsAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const reloadHrChart = useReportStore(selectReloadHrChart(keyRecoil));
  const setSelectedHrTimeAction = useReportStore((s) => s.actions.setSelectedHrTime);
  /**
   * Stores the timestamp of the heart-rate point the chart has selected. Forwards the value to the report store's setSelectedHrTime action under this tab's key.
   *
   * @param v - The selected heart-rate timestamp, or undefined to clear it.
   * @returns Nothing.
   */
  const setSelectedHrTime = (v: string | undefined) => setSelectedHrTimeAction(keyRecoil, v);
  const setViewHrModeAction = useReportStore((s) => s.actions.setViewHrMode);
  /**
   * Switches the HR summary between showing maximum and minimum heart rate. Forwards the mode to the report store's setViewHrMode action under this tab's key.
   *
   * @param v - The heart-rate view mode to activate, max or min.
   * @returns Nothing.
   */
  const setViewHrMode = (v: TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR) =>
    setViewHrModeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Stores the window portion applied when jumping to a point of the chart. Forwards the value to the report store's setJumpPortion action under this tab's key.
   *
   * @param v - The new jump portion, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);

  const chartHrRef = useRef(null);

  const [dataChart, setDataChart] = useState<any>();

  /**
   * Refreshes the heart-rate header with the min, max and average values recorded for the selected date. Clones the default HR summary, overwrites its min, max and average fields from the matching combined daily summary, then switches the view mode, selected HR time and jump date to that day's min or max entry depending on which HR tab is active.
   *
   * @param isRerender - Whether the refresh follows a re-render rather than a fresh chart load.
   * @returns Nothing.
   */
  const setMaxHrByDate = ({ isRerender = false }) => {
    // hrDataDefaultState (Recoil) is typed IHrSummary, missing the hrsOnBeat
    // field this component reads and writes locally - pre-existing gap.
    const hrDataDefaultObj: any = _.cloneDeep(getHrDataDefault());
    const foundDailySummary: any = _.find(
      getDailySummariesCombine(),
      (x) => x.date === selectedDateValue,
    );
    if (foundDailySummary) {
      hrDataDefaultObj.minHr.value = foundDailySummary.minHr?.value;
      hrDataDefaultObj.minHr.time = foundDailySummary.minHr?.time;
      hrDataDefaultObj.minHr.hrsOnBeat = foundDailySummary.minHr?.hrsOnBeat;
      hrDataDefaultObj.minHr.userUpdated = foundDailySummary.minHr?.userUpdated;
      hrDataDefaultObj.maxHr.value = foundDailySummary.maxHr?.value;
      hrDataDefaultObj.maxHr.time = foundDailySummary.maxHr?.time;
      hrDataDefaultObj.maxHr.hrsOnBeat = foundDailySummary.maxHr?.hrsOnBeat;
      hrDataDefaultObj.maxHr.userUpdated = foundDailySummary.maxHr?.userUpdated;
      hrDataDefaultObj.avgHr.value = foundDailySummary.avgHr?.value;
      hrDataDefaultObj.avgHr.userUpdated = foundDailySummary.avgHr?.userUpdated;
      // if( foundDailySummary.maxHr && !isRerender)
      if (minHrTabActive) {
        setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MIN_HR);
        setSelectedHrTime(foundDailySummary.minHr?.time);
        setJumpDate(foundDailySummary.minHr?.time);
        setJumpPortion(0);
      } else {
        setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MAX_HR);
        setSelectedHrTime(foundDailySummary.maxHr?.time);
        setJumpDate(foundDailySummary.maxHr?.time);
        setJumpPortion(0);
      }
    }
    setHrDataDefault(hrDataDefaultObj);
  };

  /**
   * Turns the day's hourly ECG summaries into the series the heart-rate chart renders. Filters the summaries to the selected day, expands them into minute-level average, max and min arrays, derives the chart bounds, maps the holter events to minute index ranges, builds the guides, missing points and artifact, AFib and other event zones, refreshes the day's HR header, and flags empty data on any gap or failure.
   *
   * @param holterEventsByDate - The artifact events of the selected date, used to draw the event indicators.
   * @param needLoading - Whether the load showed a loading state, inverted into the re-render and auto-zoom hints.
   * @returns Nothing.
   */
  const formatHrData = (holterEventsByDate: any[] = [], needLoading?: boolean) => {
    try {
      let hasNoData;
      const selectedDateMoment = convertToMomentJs(selectedDateValue, timezoneOffset);
      const { data, channels, samplingFrequency } = ecgDataMap;

      if (data?.length && selectedDateMoment && !data.find((x: any) => !x.dataUrl)) {
        const startDayMoment = selectedDateMoment.clone().startOf('days');
        const stopDayMoment = selectedDateMoment.clone().endOf('days');
        // *: Filter HR data in date
        // filterHourlySummaryRanges's start/stop (handler.ts, Task 7) are typed number, but every
        // call site here passes a Moment - pre-existing.
        const currentDateData = filterHourlySummaryRanges({
          summaries: ecgDataMap,
          start: startDayMoment,
          stop: stopDayMoment,
          channels,
          samplingFrequency,
          willAddDataRanges: false,
        } as any);
        const isValidCurrentDateData = validateEcgDataMap(currentDateData);
        if (isValidCurrentDateData) {
          const filteredAvgHrArray: any[] = [];
          const filteredMaxHrArray: any[] = [];
          const filteredMinHrArray: any[] = [];
          _.forEach(currentDateData, (item: any) => {
            filteredAvgHrArray.push(item.avgHrs);
            filteredMaxHrArray.push(item.maxHrs);
            filteredMinHrArray.push(item.minHrs);
          });
          // createMinutelyHrChartData's return type (handler.ts, Task 7) is a union of a flat array
          // and an array-of-arrays depending on its args, which the destructure below can't narrow - pre-existing.
          const [avgHrArray, maxHrArray, minHrArray]: any[] = createMinutelyHrChartData({
            hourlySummaries: currentDateData,
            minutelyHrs: filteredAvgHrArray,
            start: startDayMoment,
            stop: stopDayMoment,
            maxMinutelyHrs: filteredMaxHrArray,
            minMinutelyHrs: filteredMinHrArray,
            isEditReportPage: true,
          }) as any;

          if (_.find(avgHrArray, (x) => !_.isNil(x))) {
            // *: All data are null
            let minHr = 0;
            let maxHr = 150;
            const clonedHrArray = [...avgHrArray];
            _.remove(clonedHrArray, (x) => x < 0);
            if (clonedHrArray.length > 0) {
              const minHrTmp = Math.floor(_.min(clonedHrArray) / 10) * 10;
              const maxHrTmp = Math.ceil(_.max(clonedHrArray) / 10) * 10;
              const { min, max } = getMinMaxChart(minHrTmp, maxHrTmp);
              minHr = min || 0;
              maxHr = max || 150;
            }

            const firstEpoch = startDayMoment.valueOf();
            const eventIndicators = _.map(holterEventsByDate, (holterEvent) => {
              const roundStartMoment = moment(holterEvent.start).startOf('minutes');
              const roundStopMoment = moment(holterEvent.stop).startOf('minutes');
              return {
                ...holterEvent,
                startIndex: (roundStartMoment.valueOf() - firstEpoch) / 1000 / 60,
                stopIndex: (roundStopMoment.valueOf() - firstEpoch) / 1000 / 60,
              };
            });
            const {
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
            } = createChartData({
              hrArray: avgHrArray,
              eventIndicators,
              firstEpoch,
              maxHr, //* use max hr to fill null data and draw zone
              maxHrArray,
              minHrArray,
              isEditReportPage: true,
            });

            setMaxHrByDate({
              isRerender: !needLoading,
            });

            setDataChart({
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
              minHr,
              maxHr,
              eventIndicators,
              firstEpoch,
              isZoomAfterDrawChart: !needLoading,
            });
          } else {
            hasNoData = true;
          }
        } else {
          hasNoData = true;
        }
      } else {
        hasNoData = true;
      }
      if (hasNoData) {
        setIsEmptyData(true);
        setIsLoadingHrChart(false);
      }
    } catch (error) {
      logError('Failed to fetch HR data: ', error);
      toastrError(error.message, 'Error');
      setIsEmptyData(true);
      setIsLoadingHrChart(false);
    }
  };

  /**
   * Loads the artifact events of the selected date and feeds them into the heart-rate chart. Queries holter events for the study, profile and one-day window, drops the loading flag early when the request returns within 100 milliseconds, formats the events and merges pending event changes and new events into them, stores the combined list, and hands it to the chart formatting step.
   *
   * @param needLoading - Whether to raise the chart loading flag before fetching, also passed on as the re-render hint.
   * @returns A promise resolving once the events are stored and the chart data has been formatted.
   */
  const fetchHolterEventsByDate = async (needLoading = true) => {
    if (needLoading) {
      setIsLoadingHrChart(true);
    }
    let downloadedHolterEvents: any[] = [];
    try {
      const start = performance.now();
      const startSearchDate = selectedDateValue;
      const stopSearchDate = selectedDateValue
        ? moment(selectedDateValue).clone().add(1, 'days').toISOString()
        : undefined;
      const holterEventsFilter = {
        studyId,
        profileId,
        types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        startSearchDate,
        stopSearchDate,
      };
      const holterEvents = await fetchHolterEvents(
        holterEventsFilter,
        0,
        true,
        KEY_CANCEL.API_HOLTER_AI,
      );
      if (performance.now() - start < 100) {
        setIsLoadingHrChart(false);
      }
      if (holterEvents?.events) {
        downloadedHolterEvents = holterEvents.events;
      }
      const dataFormat = formatEventStrip(downloadedHolterEvents);
      const holterEventsByDate = updateEventChangeAndNew(
        dataFormat,
        eventChanges,
        eventNews,
        startSearchDate,
        stopSearchDate,
        [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
      );
      setCombinedEvents(holterEventsByDate);
      formatHrData(holterEventsByDate, needLoading);
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    if (selectedDateValue) {
      chartHrRef.current.resetCurrentSelect();
      fetchHolterEventsByDate();
    }
  }, [selectedDateValue, reloadHrChart]);

  useUpdateEffect(() => {
    if (selectedDateValue) {
      fetchHolterEventsByDate();
    }
  }, [ecgDataMap]);

  // *: Fetch and re-draw events
  useUpdateEffect(() => {
    if (selectedDateValue) {
      fetchHolterEventsByDate(false);
    }
  }, [eventChanges, eventNews]);

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext) => {
      if (chartHrRef.current) {
        chartHrRef.current.handleClickArrowButton(isNext);
      }
    },
  }));

  return {
    keyRecoil,
    chartHrRef,
    dataChart,
  };
};

export default useDailySummaryHrChart;
